import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, model, OnInit, Output, Signal, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ActivityTaskStatus } from '../../../../tokens-and-interfaces';
import { TranslationModule } from '../../../../i18n/translation-module';
import { ActivityService, CrmFieldService, StandardIds } from '../../../../shared-services';
import { ActivityInterface, FieldMaskInterface } from '@vendasta/crm';
import { User } from '@vendasta/iamv2';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { firstValueFrom } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ReactiveFormsModule } from '@angular/forms';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { SanitizeHtmlPipe } from '../../activities/sanitize-html.pipe';
import { CrmObjectIdentifier } from '../../activities/activity.interface';
import { MatIconButton } from '@angular/material/button';

@Component({
  selector: 'crm-activity-task-v2',
  templateUrl: './task-v2.component.html',
  styleUrls: ['./task-v2.component.scss'],
  providers: [],
  imports: [
    CommonModule,
    TranslateModule,
    TranslationModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
    MatInputModule,
    MatIconModule,
    SanitizeHtmlPipe,
    MatIconButton,
  ],
})
export class CrmActivityTaskV2Component implements OnInit {
  private unknownNamePlaceholder = 'CONTACT.ACTIVITIES.UNKNOWN_NAME';
  protected taskCompleted = signal<boolean>(false);

  taskOverdue = model<boolean>(false);

  activity = model<ActivityInterface>({});
  users = model<User[] | undefined>(undefined);
  associatedObject = model<CrmObjectIdentifier | undefined>(undefined);

  @Output() activityChange = new EventEmitter<ActivityInterface>();

  cardInfo: Signal<ActivityCardInformation> = computed(() => {
    return {
      crmObjectId: this.activity().crmObjectId,
      title: this.getFieldData(this.activity(), StandardIds.ActivityName),
      body: this.getFieldData(this.activity(), StandardIds.ActivityTaskBody),
      status: this.getFieldData(this.activity(), StandardIds.ActivityTaskStatus),
      type: this.getFieldData(this.activity(), StandardIds.ActivityTaskType),
      priority: this.getFieldData(this.activity(), StandardIds.ActivityTaskPriority),
      assignee: this.getAssigneeName(this.activity()),
    };
  });

  ngOnInit(): void {
    const taskIsCompleted =
      this.activity()?.fields?.find((field) => field.fieldId === StandardIds.ActivityTaskStatus)?.stringValue ===
      ActivityTaskStatus.Completed;
    this.taskCompleted.set(taskIsCompleted);
  }

  private getFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }

  private getAssigneeName(activity: ActivityInterface): string {
    const ownerId = activity?.ownerId ?? '';
    const user = this.users()?.find((user) => user.userId === ownerId);
    const userName = [user?.firstName, user?.lastName]
      .map((name) => name?.trim())
      .filter((name) => name)
      .join(' ');
    return userName || this.unknownNamePlaceholder;
  }

  private getTaskCompletionStatus(): string {
    return this.taskCompleted() ? ActivityTaskStatus.Open : ActivityTaskStatus.Completed;
  }

  private emitStatusChange(status: string): void {
    const activity = this.activity();
    const field = this.fieldService.getFieldValueFromCrmObject(activity, StandardIds.ActivityTaskStatus);
    if (field) {
      field.stringValue = status;
    } else {
      activity.fields?.push({ fieldId: StandardIds.ActivityTaskStatus, stringValue: status });
    }
    this.activity.set(activity);
    this.taskCompleted.set(!this.taskCompleted());
    this.activityChange.emit(activity);
  }

  async updateTaskCompletionStatus(): Promise<void> {
    const newStatus = this.getTaskCompletionStatus();
    const activity: ActivityInterface = {
      crmObjectId: this.activity().crmObjectId,
      crmObjectSubtype: this.activity().crmObjectSubtype,
      fields: [{ fieldId: StandardIds.ActivityTaskStatus, stringValue: newStatus }],
    };

    const mask: FieldMaskInterface = {
      paths: [StandardIds.ActivityTaskStatus],
    };
    try {
      await firstValueFrom(this.activityService.updateActivity(activity, mask));
      this.snack.openSuccessSnack('TASK.STATUS_UPDATE.SUCCESS');
      this.emitStatusChange(newStatus);
    } catch (_) {
      this.snack.openErrorSnack('TASK.STATUS_UPDATE.ERROR');
    }
  }

  constructor(
    private readonly fieldService: CrmFieldService,
    private readonly activityService: ActivityService,
    private readonly snack: SnackbarService,
  ) {}
}
interface ActivityCardInformation {
  title: string;
  body: string;
  status: string;
  type: string;
  priority: string;
  assignee: string;
}
